/**
 * src/features/auth/ui/MagicLinkForm.tsx
 *
 * Magic-link sign-in form — moved from src/pages/landing/AuthFormCard.tsx (Phase 42).
 * Manages step (form → sent) and email input state with animated transitions between
 * steps. Behavior is UNCHANGED from the original AuthFormCard.tsx (AUTH-09 regression
 * contract): real <form onSubmit>, Enter submits natively, focus on mount, same i18n
 * keys, same defensive try/catch around signIn().
 *
 * Only markup classes changed (42-CONTEXT.md Область 5-бис): dead Tailwind tokens
 * (text-destructive / text-foreground / bg-background / text-muted-foreground — they
 * register zero CSS rules in this project's tailwind.config.js) replaced with live
 * classes — see 42-UI-SPEC.md Color section / 42-PATTERNS.md MagicLinkForm §6.
 *
 * What did NOT move here: the `landing.form_label` caption and <RefereeCallout /> — both
 * are shared across the whole email/Google composition now and live in LoginCard.tsx.
 */

import { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail } from 'lucide-react';
import Button from '@/shared/ui/button';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';
// Intra-slice import: relative path, NOT '@/features/auth'. This component is itself one
// of the barrel's exports, so importing useAuth from the barrel here would close a
// module cycle. CONTEXT.md Область 1's "only through index.ts" rule targets imports FROM
// OUTSIDE the slice (i.e. AuthFormCard.tsx), not internal features/auth/ui <-> hooks wiring.
import { useAuth } from '../hooks/useAuth';
import { setLastLoginMethod } from '../lib/lastLoginMethod';

export const MagicLinkForm: FC = () => {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const reduced = useReducedMotion();
  const shortDuration = reduced ? 0 : 0.2;

  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'form' | 'sent'>('form');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Focus email input on mount (UI-SPEC focus management) — unchanged from source.
  const emailRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsLoading(true);
    setError(null);
    try {
      // WR-01: better-auth returns { data, error } and NEVER throws. Check result.error.
      const result = await signIn(email.trim());
      if (result?.error) {
        setError(t('landing.send_error', 'Failed to send sign-in link. Please try again.'));
      } else {
        // AUTH-09: write at "sent", not "signed in" — magic-link has no client-side moment
        // of a confirmed session before the user follows the link in their inbox, and
        // 42-UI-SPEC.md §2 fixes this exact point as the success point to record.
        setLastLoginMethod('magic-link');
        setStep('sent');
      }
    } catch {
      setError(t('landing.send_error', 'Failed to send sign-in link. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {step === 'form' ? (
        <motion.div
          key="form"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: shortDuration }}
        >
          {/* Real <form onSubmit>, NOT <div onClick> — three real-loop e2e specs submit via
              emailInput.press('Enter') and never click the button (42-RESEARCH.md Pitfall
              2). The root element of this branch must stay a <form>. */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-body font-medium">
                {t('auth.email.label', 'Email')}
              </span>
              <input
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.email.placeholder', 'you@example.com')}
                required
                autoComplete="email"
                disabled={isLoading}
                className="border border-slate-200 dark:border-slate-700 rounded-input px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 disabled:opacity-50"
              />
            </label>
            <Button
              variant="default"
              size="lg"
              type="submit"
              className="w-full"
              disabled={isLoading || !email.trim()}
              aria-busy={isLoading}
            >
              {isLoading
                ? t('auth.submit_loading', 'Отправляем...')
                : t('landing.cta', 'Start free — sign in with email')}
            </Button>
            {error && (
              <p className="text-body text-red-600 dark:text-red-400 text-center" role="alert">
                {error}
              </p>
            )}
          </form>
        </motion.div>
      ) : (
        <motion.div
          key="sent"
          className="flex flex-col gap-3 text-center py-2"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: shortDuration }}
        >
          <Mail
            size={32}
            className="mx-auto text-blue-600 dark:text-blue-400"
            aria-hidden="true"
          />
          <p className="text-subhead font-bold text-slate-900 dark:text-slate-100">
            {t('auth.sent.title', 'Проверьте почту')}
          </p>
          <p className="text-body text-slate-500 dark:text-slate-400">
            {t('auth.sent.body', 'Мы отправили ссылку для входа на {{email}}. Перейдите по ней, чтобы войти.', { email })}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
